import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Calculator, 
  FileText, 
  Package, 
  Settings, 
  Users,
  BarChart3,
  X
} from 'lucide-react';
import { cn } from '@plvplus/core';
import { useUserRoles } from '../../hooks/useUserRoles';
import { AppRoleGuard } from '../AppRoleGuard';

interface AppSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const navigation = [
  { 
    name: 'Tableau de bord', 
    href: '/dashboard', 
    icon: BarChart3,
    permission: { module: 'dashboard', action: 'read' }
  },
  { 
    name: 'PRI+ (Prix)', 
    href: '/pri-plus', 
    icon: Calculator,
    permission: { module: 'pri-plus', action: 'read' }
  },
  { 
    name: 'FACT+ (Facturation)', 
    href: '/fact-plus', 
    icon: FileText,
    permission: { module: 'fact-plus', action: 'read' }
  },
  { 
    name: 'Inventaire', 
    href: '/inventory', 
    icon: Package,
    permission: { module: 'inventory', action: 'read' }
  },
  { 
    name: 'Utilisateurs', 
    href: '/users', 
    icon: Users,
    requireAdmin: true
  },
  { 
    name: 'Paramètres', 
    href: '/settings', 
    icon: Settings,
    permission: { module: 'settings', action: 'read' }
  },
];

export const AppSidebar: React.FC<AppSidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user } = useUserRoles();

  return (
    <div className={cn(
      'fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0',
      isOpen ? 'translate-x-0' : '-translate-x-full'
    )}>
      {/* Header */}
      <div className="flex h-16 items-center justify-between px-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">PLV</span>
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">PLV+</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">ERP Modulaire</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="lg:hidden p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* User Info */}
      {user && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
              <span className="text-primary-600 dark:text-primary-400 font-medium text-sm">
                {user.name.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {user.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user.roles[0]?.name || 'Utilisateur'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="mt-6 px-3">
        <div className="space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href || 
                            (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
            
            const NavigationItem = (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                  isActive
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-300'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                )}
                onClick={onClose}
              >
                <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                {item.name}
              </Link>
            );

            if (item.requireAdmin) {
              return (
                <AppRoleGuard key={item.name} requireAdmin>
                  {NavigationItem}
                </AppRoleGuard>
              );
            }

            if (item.permission) {
              return (
                <AppRoleGuard key={item.name} requiredPermission={item.permission}>
                  {NavigationItem}
                </AppRoleGuard>
              );
            }

            return NavigationItem;
          })}
        </div>
      </nav>

      {/* Footer */}
      <div className="absolute bottom-4 left-0 right-0 px-3">
        <div className="text-center text-xs text-gray-500 dark:text-gray-400">
          <p>PLV+ v1.0.0</p>
          <p>© 2024 TSY9INE</p>
        </div>
      </div>
    </div>
  );
};
